// /admin/tenants/:id — operator console: one client. Header (status, Publicar,
// Editar, Suspender, Eliminar) + tabs: Resumen, Agentes, Canales, Agenda and
// Catálogo (healthcare only), Conversaciones, Equipo, Facturación (+CFDI),
// Privacidad — all built and live. Data via the getTenant() seam.
//
// The active tab lives in the URL (?tab=) so every cross-surface deep link —
// the CFDI queue, the dunning list, attention items — can land an operator on
// the exact section, and staff can share "client X's Facturación" as a URL
// instead of as instructions.

import { useQuery, useQueryClient } from '@tanstack/react-query'
// Subpath import, never the @vysto/shared barrel — that pulls jsonwebtoken and
// pino and crashes the browser.
import { CFDI_USES, FISCAL_REGIMENS } from '@vysto/shared/fiscal'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { getInvoicePayLink, getTenantCfdis, type TenantCfdiInvoice } from './cfdi-queue-data.js'
import { CfdiPanel } from './CfdiPanel.js'
import {
  BOT_STATUS_LABEL,
  checkWhatsappHealth,
  connectChannel,
  connectWhatsapp,
  createWorker,
  deleteTenant,
  deleteWorker,
  disconnectChannel,
  disconnectWhatsapp,
  disconnectWorkerNumber,
  getNumberBinding,
  getTenant,
  getTenantMembers,
  getWhatsappStatus,
  goLiveTenant,
  listOrgUsers,
  listConnectedChannels,
  listProviders,
  createProvider,
  updateProvider,
  getProviderSchedule,
  saveProviderSchedule,
  addProviderTimeOff,
  deleteProviderTimeOff,
  listCatalog,
  createCatalogItem,
  updateCatalogItem,
  cancelTenantSubscription,
  listTenantConversations,
  getBusinessProfile,
  getTenantSubscription,
  createTenantSubscription,
  getGoLiveReadiness,
  type GoLiveReadiness,
  inviteTenantMember,
  changeTenantMemberRole,
  removeTenantMember,
  patchTenantSubscription,
  saveBusinessProfile,
  type BusinessProfile,
  type BusinessProfilePatch,
  type CatalogItem,
  type Provider,
  type ProviderTimeOff,
  listWhatsappTemplates,
  listWorkerTemplates,
  listWorkers,
  type ConnectedChannel,
  type MetaChannel,
  type NumberBinding,
  PLAN_LABEL,
  reactivateTenant,
  releaseNumber,
  resendOwnerInvite,
  sendWhatsappTest,
  removeWhatsappTemplate,
  setWhatsappTemplate,
  setWorkerStatus,
  STATUS_BADGE,
  suspendTenant,
  type TenantDetail,
  type TenantMember,
  type TenantPlan,
  type TenantVertical,
  updateTenant,
  VERTICAL_LABEL,
  type Worker,
} from './tenants-data.js'
import {
  Banner,
  Button,
  Card,
  Chip,
  DropdownMenu,
  EmptyStateCard,
  FormField,
  Input,
  KpiTile,
  Modal,
  NumberInput,
  PageHeader,
  Select,
  Spinner,
  StatGrid,
  Tabs,
  TabPanel,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import {
  buildWeek,
  type DayBlocks,
  fmtDateTime,
  MultiBlockWeekEditor,
  serializeWeek,
} from '../agenda/schedule-editor.js'
import { ConsentSettings } from '../settings/ConsentSettings.js'
import { PrivacyRequests } from '../settings/PrivacyRequests.js'

// Unbuilt tabs are disabled (not clickable) so they read as "coming" rather than
// live-but-broken. Enable each one as its sub-view lands.
const TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'bots', label: 'Agentes' },
  { id: 'identities', label: 'Canales' },
  { id: 'conversations', label: 'Conversaciones' },
  { id: 'team', label: 'Equipo' },
  { id: 'billing', label: 'Facturación' },
  // Its own tab rather than living under Canales with the consent notice:
  // privacy is a compliance file with deadlines, not a channel setting, and
  // it is about to hold more than one panel.
  { id: 'privacy', label: 'Privacidad' },
]

// The Agenda (providers) and Catálogo tabs only make sense for healthcare
// tenants — the same vertical gating the owner nav applies (HEALTHCARE_ONLY_NAV
// hides both for other verticals). Slotted after Canales, before Conversaciones.
const HEALTHCARE_VERTICALS = new Set(['salud', 'medico'])

/** Every tab id that can ever exist, vertical-injected ones included — the URL
 *  validator runs before the tenant (and its vertical) has loaded. */
const ALL_TAB_IDS = new Set([...TABS.map((t) => t.id), 'agenda', 'catalog'])
function tabsFor(vertical: TenantVertical): typeof TABS {
  if (!HEALTHCARE_VERTICALS.has(vertical)) return TABS
  const i = TABS.findIndex((t) => t.id === 'conversations')
  return [
    ...TABS.slice(0, i),
    { id: 'agenda', label: 'Agenda' },
    { id: 'catalog', label: 'Catálogo' },
    ...TABS.slice(i),
  ]
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
}

function MetaRow({ k, children }: { k: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-line py-2.5 last:border-b-0">
      <span className="text-[12px] text-text-2">{k}</span>
      <span className="text-[12.5px] font-medium text-text">{children}</span>
    </div>
  )
}

function Overview({ tenant }: { tenant: TenantDetail }): JSX.Element {
  const status = STATUS_BADGE[tenant.status]
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr,260px]">
      <div className="flex flex-col gap-5">
        <StatGrid columns={3}>
          <KpiTile
            label="Conversaciones (30 d)"
            value={tenant.conversations30d.toLocaleString('es-MX')}
          />
          <KpiTile label="Sin intervención humana" value={`${tenant.noHumanRate}%`} />
          <KpiTile label="Tiempo de respuesta" value={`${tenant.responseTimeS} s`} />
        </StatGrid>
        <BusinessProfilePanel tenantId={tenant.id} />
      </div>

      <Card>
        <Card.Head title="Información del cliente" />
        <Card.Body>
          <MetaRow k="Vertical">{VERTICAL_LABEL[tenant.vertical]}</MetaRow>
          <MetaRow k="Plan">{PLAN_LABEL[tenant.plan]}</MetaRow>
          <MetaRow k="Estado">
            {tenant.suspended ? (
              <Chip tone="warn" dot>
                Suspendido
              </Chip>
            ) : (
              <Chip tone={status.tone} dot>
                {status.label}
              </Chip>
            )}
          </MetaRow>
          <MetaRow k="Dueño">
            {tenant.ownerEmail ? (
              <span className="flex flex-col items-end leading-tight">
                {tenant.ownerName ? <span>{tenant.ownerName}</span> : null}
                <a
                  className="text-[11.5px] font-normal text-link hover:underline"
                  href={`mailto:${tenant.ownerEmail}`}
                >
                  {tenant.ownerEmail}
                </a>
              </span>
            ) : (
              <span className="text-text-2">Sin Dueño</span>
            )}
          </MetaRow>
          <MetaRow k="Gerente de cuenta">{tenant.accountManager.name}</MetaRow>
          <MetaRow k="Creado">{formatDate(tenant.createdAt)}</MetaRow>
        </Card.Body>
      </Card>
    </div>
  )
}

// The fiscal + contact identity WE register for a client. Legal name + RFC feed
// invoicing (Facturama); the owner sees them locked on their Settings page. All
// verticals have one, so it lives on Resumen (not a healthcare-only tab).
function BusinessProfilePanel({ tenantId }: { tenantId: string }): JSX.Element {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'business-profile'],
    queryFn: () => getBusinessProfile(tenantId),
  })
  const [editing, setEditing] = useState(false)

  return (
    <Card>
      <Card.Head
        title="Perfil del negocio"
        action={
          <Button
            variant="secondary"
            onClick={() => setEditing(true)}
            disabled={isLoading || !data}
          >
            Editar
          </Button>
        }
      />
      <Card.Body>
        {isError ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-[12.5px] text-text-2">No se pudo cargar el perfil del negocio.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : isLoading || !data ? (
          <Spinner label="Cargando…" />
        ) : (
          <>
            <MetaRow k="Nombre legal">{data.legalName ?? <Muted>Por registrar</Muted>}</MetaRow>
            <MetaRow k="RFC">
              {data.rfc ? (
                <span className="font-mono">{data.rfc}</span>
              ) : (
                <Muted>Por registrar</Muted>
              )}
            </MetaRow>
            <MetaRow k="Domicilio fiscal">
              {data.fiscalPostalCode ? (
                <span>
                  {[data.fiscalAddress, data.fiscalCity, data.fiscalState]
                    .filter(Boolean)
                    .join(', ')}
                  {data.fiscalAddress || data.fiscalCity || data.fiscalState ? ' · ' : ''}
                  CP <span className="font-mono">{data.fiscalPostalCode}</span>
                </span>
              ) : (
                <Muted>Por registrar</Muted>
              )}
            </MetaRow>
            <MetaRow k="Correo de facturación">
              {data.contactEmail ?? <Muted>Sin registrar</Muted>}
            </MetaRow>
            <MetaRow k="Zona horaria">
              {data.timezone ?? <Muted>Ciudad de México (GMT-6)</Muted>}
            </MetaRow>
          </>
        )}
      </Card.Body>

      {editing && data ? (
        <BusinessProfileModal
          tenantId={tenantId}
          profile={data}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </Card>
  )
}

function Muted({ children }: { children: ReactNode }): JSX.Element {
  return <span className="text-text-3">{children}</span>
}

function BusinessProfileModal({
  tenantId,
  profile,
  onClose,
}: {
  tenantId: string
  profile: BusinessProfile
  onClose: () => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const [legalName, setLegalName] = useState(profile.legalName ?? '')
  const [rfc, setRfc] = useState(profile.rfc ?? '')
  const [contactEmail, setContactEmail] = useState(profile.contactEmail ?? '')
  const [timezone, setTimezone] = useState(profile.timezone ?? '')
  const [postal, setPostal] = useState(profile.fiscalPostalCode ?? '')
  const [city, setCity] = useState(profile.fiscalCity ?? '')
  const [stateName, setStateName] = useState(profile.fiscalState ?? '')
  const [address, setAddress] = useState(profile.fiscalAddress ?? '')
  const [regimen, setRegimen] = useState(profile.fiscalRegimen ?? '')
  const [cfdiUse, setCfdiUse] = useState(profile.cfdiUse ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function save(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      // Send ONLY what changed. This used to send every field on every save, so
      // opening the modal and saving with a box left blank actively CLEARED
      // that config key — a fiscal identity you can wipe by looking at it.
      const next: Record<string, string> = {
        legalName: legalName.trim(),
        rfc: rfc.trim().toUpperCase(),
        contactEmail: contactEmail.trim(),
        timezone: timezone.trim(),
        fiscalPostalCode: postal.trim(),
        fiscalCity: city.trim(),
        fiscalState: stateName.trim(),
        fiscalAddress: address.trim(),
        fiscalRegimen: regimen.trim(),
        cfdiUse: cfdiUse.trim(),
      }
      const before: Record<string, string> = {
        legalName: profile.legalName ?? '',
        rfc: profile.rfc ?? '',
        contactEmail: profile.contactEmail ?? '',
        timezone: profile.timezone ?? '',
        fiscalPostalCode: profile.fiscalPostalCode ?? '',
        fiscalCity: profile.fiscalCity ?? '',
        fiscalState: profile.fiscalState ?? '',
        fiscalAddress: profile.fiscalAddress ?? '',
        fiscalRegimen: profile.fiscalRegimen ?? '',
        cfdiUse: profile.cfdiUse ?? '',
      }
      const patch: BusinessProfilePatch = Object.fromEntries(
        Object.entries(next).filter(([k, v]) => v !== before[k]),
      )
      if (Object.keys(patch).length === 0) {
        onClose()
        return
      }
      await saveBusinessProfile(tenantId, patch)
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'tenant', tenantId, 'business-profile'],
      })
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo guardar el perfil.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Perfil del negocio"
      subtitle="Datos fiscales y de contacto del cliente. El nombre legal y el RFC se usan para su facturación."
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* The placeholder used to read "Clínica del Valle S.A. de C.V.", which
            taught the operator to type the one thing CFDI 4.0 rejects: the SAT
            padrón holds the razón social WITHOUT the régimen societal. That
            suffix is what made the first real stamp fail. */}
        <FormField
          label="Nombre legal"
          htmlFor="bp-legal"
          required="optional"
          helper="Como aparece en su constancia del SAT, sin S.A. de C.V. ni S.C."
        >
          <Input
            id="bp-legal"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            placeholder="Clínica del Valle"
          />
        </FormField>
        <FormField
          label="RFC"
          htmlFor="bp-rfc"
          required="optional"
          helper="12 caracteres si es empresa, 13 si es persona física."
        >
          <Input
            id="bp-rfc"
            value={rfc}
            onChange={(e) => setRfc(e.target.value.toUpperCase())}
            placeholder="CDV180429TM6"
            className="font-mono"
          />
        </FormField>
        {/* Both are MANDATORY on a CFDI 4.0 receptor and neither existed
            anywhere before. Codes come from the SAT catalogs in
            @vysto/engine so the operator form, the clinic's own form and the
            stamping code all read one list. */}
        <FormField
          label="Régimen fiscal"
          htmlFor="bp-regimen"
          required="optional"
          helper="El de su constancia del SAT. Debe corresponder al tipo de RFC."
        >
          <Select
            id="bp-regimen"
            value={regimen}
            onChange={(e) => setRegimen(e.target.value)}
            options={[
              { value: '', label: 'Sin registrar' },
              ...FISCAL_REGIMENS.map((r) => ({ value: r.code, label: `${r.code} — ${r.name}` })),
            ]}
          />
        </FormField>
        <FormField
          label="Uso del CFDI"
          htmlFor="bp-uso"
          required="optional"
          helper="Para qué usará la factura. Casi siempre Gastos en general."
        >
          <Select
            id="bp-uso"
            value={cfdiUse}
            onChange={(e) => setCfdiUse(e.target.value)}
            options={[
              { value: '', label: 'Sin registrar' },
              ...CFDI_USES.map((u) => ({ value: u.code, label: `${u.code} — ${u.name}` })),
            ]}
          />
        </FormField>
        <FormField
          label="Código postal fiscal"
          htmlFor="bp-cp"
          required="optional"
          helper="Habilita el pago por transferencia (SPEI) y es obligatorio para la factura fiscal."
        >
          <Input
            id="bp-cp"
            value={postal}
            onChange={(e) => setPostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="06600"
            inputMode="numeric"
            className="font-mono"
          />
        </FormField>
        <FormField label="Calle y número" htmlFor="bp-addr" required="optional">
          <Input
            id="bp-addr"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Av. Insurgentes Sur 1234"
          />
        </FormField>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <FormField label="Ciudad" htmlFor="bp-city" required="optional">
            <Input
              id="bp-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ciudad de México"
            />
          </FormField>
          <FormField label="Estado" htmlFor="bp-state" required="optional">
            <Input
              id="bp-state"
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              placeholder="CDMX"
            />
          </FormField>
        </div>
        <FormField label="Correo de facturación" htmlFor="bp-email" required="optional">
          <Input
            id="bp-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="facturacion@clinica.mx"
          />
        </FormField>
        <FormField label="Zona horaria" htmlFor="bp-tz" required="optional">
          <Input
            id="bp-tz"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Mexico_City"
          />
        </FormField>
        {error ? <p className="text-[12px] text-sens">{error}</p> : null}
      </div>
    </Modal>
  )
}

function WorkersPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: workers, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'workers'],
    queryFn: () => listWorkers(tenantId),
  })
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [customName, setCustomName] = useState('')
  const [customRole, setCustomRole] = useState('')
  // Per-worker WhatsApp connect (targets a specific worker via botId).
  const [connectFor, setConnectFor] = useState<Worker | null>(null)
  // Set when a connect 409s because the number is owned elsewhere — surfaces
  // which client has it + a free-and-retry button.
  const [conflict, setConflict] = useState<NumberBinding | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Worker | null>(null)
  const [form, setForm] = useState({
    displayNumber: '',
    phoneNumberId: '',
    wabaId: '',
    accessToken: '',
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'worker-templates'],
    queryFn: () => listWorkerTemplates(tenantId),
    enabled: adding,
  })

  function resetAdd(): void {
    setTemplateId('')
    setCustomName('')
    setCustomRole('')
    setError('')
  }

  async function refresh(): Promise<void> {
    // Invalidate both the workers list AND the tenant detail — the Resumen tab
    // reads the bot list from getTenant(), so a create/delete must refresh it
    // too or a removed worker lingers there.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'workers'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] }),
    ])
  }

  async function submitAdd(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await createWorker(
        tenantId,
        templateId
          ? { workerTemplateId: templateId }
          : { name: customName.trim(), role: customRole.trim() },
      )
      await refresh()
      setAdding(false)
      resetAdd()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo crear el agente.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitConnect(): Promise<void> {
    if (!connectFor) return
    setBusy(true)
    setError('')
    setConflict(null)
    try {
      const res = await connectWhatsapp(tenantId, {
        displayNumber: form.displayNumber.trim(),
        phoneNumberId: form.phoneNumberId.trim(),
        wabaId: form.wabaId.trim(),
        accessToken: form.accessToken.trim(),
        botId: connectFor.id,
      })
      await refresh()
      if (!res.webhookSubscribed) {
        setError('Número guardado, pero no se pudo suscribir el webhook en Meta. Revisa el token.')
        return
      }
      setConnectFor(null)
      setForm({ displayNumber: '', phoneNumberId: '', wabaId: '', accessToken: '' })
    } catch (err) {
      const code = isAxiosError(err)
        ? (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code
        : undefined
      if (code === 'number_already_connected') {
        // Surface who owns it + offer a one-click free-and-retry.
        try {
          setConflict(await getNumberBinding(form.phoneNumberId.trim()))
        } catch {
          setError('Ese número ya está conectado a otro cliente.')
        }
      } else {
        setError(apiErrorMessage(err, 'No se pudo conectar. Revisa las credenciales de Meta.'))
      }
    } finally {
      setBusy(false)
    }
  }

  async function releaseAndRetry(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await releaseNumber(form.phoneNumberId.trim())
      setConflict(null)
      await submitConnect()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo liberar el número.'))
      setBusy(false)
    }
  }

  async function disconnect(w: Worker): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await disconnectWorkerNumber(tenantId, w.id)
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo desconectar el número.'))
    } finally {
      setBusy(false)
    }
  }

  async function togglePause(w: Worker): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await setWorkerStatus(tenantId, w.id, w.status === 'paused' ? 'active' : 'paused')
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo cambiar el estado del agente.'))
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return
    setBusy(true)
    setError('')
    try {
      await deleteWorker(tenantId, deleteTarget.id)
      await refresh()
      setDeleteTarget(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo eliminar el agente.'))
    } finally {
      setBusy(false)
    }
  }

  const addValid = templateId !== '' || customName.trim() !== ''
  const connectValid =
    form.displayNumber.trim() !== '' &&
    form.phoneNumberId.trim() !== '' &&
    form.wabaId.trim() !== '' &&
    form.accessToken.trim() !== ''

  if (isLoading) return <Spinner label="Cargando…" />

  return (
    <Card>
      <Card.Head
        title="Agentes"
        action={<Button onClick={() => setAdding(true)}>Agregar agente</Button>}
      />
      <Card.Body>
        {workers && workers.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {workers.map((w) => {
              const s = BOT_STATUS_LABEL[w.status]
              return (
                <li
                  key={w.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[12.5px] font-semibold text-text">{w.name}</span>
                    <span className="text-[11px] text-text-2">{w.role || 'Sin rol'}</span>
                  </span>
                  <span className="ml-auto flex flex-wrap items-center gap-2">
                    <Chip tone={s.tone} dot>
                      {s.label}
                    </Chip>
                    {w.connected ? (
                      <>
                        <span className="font-mono text-[10.5px] text-text-2">{w.number}</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void disconnect(w)}
                          disabled={busy}
                        >
                          Desconectar
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => setConnectFor(w)}>
                        Conectar número
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void navigate(`/admin/tenants/${tenantId}/bots/${w.id}/flows`)}
                    >
                      Conversación
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void navigate(`/admin/tenants/${tenantId}/bots/${w.id}`)}
                    >
                      Configurar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void togglePause(w)}
                      disabled={busy}
                    >
                      {w.status === 'paused' ? 'Reanudar' : 'Pausar'}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteTarget(w)}>
                      Eliminar
                    </Button>
                  </span>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-[12.5px] text-text-2">
            Este cliente aún no tiene agentes. Agrega el primero para empezar.
          </p>
        )}
      </Card.Body>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Agregar agente"
        subtitle="Elige una plantilla de la industria del cliente o crea uno a la medida."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void submitAdd()} loading={busy} disabled={!addValid}>
              Crear
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Plantilla" htmlFor="wk-tpl">
            <Select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              placeholder="Elige una plantilla…"
              options={templates.map((t) => ({ value: t.id, label: `${t.name} · ${t.role}` }))}
            />
          </FormField>
          <p className="text-[11px] text-text-2">o crea uno a la medida:</p>
          <FormField label="Nombre" htmlFor="wk-name">
            <Input
              value={customName}
              onChange={(e) => {
                setCustomName(e.target.value)
                if (e.target.value) setTemplateId('')
              }}
              placeholder="p. ej. Cobranza"
            />
          </FormField>
          <FormField label="Rol" htmlFor="wk-role">
            <Input
              value={customRole}
              onChange={(e) => setCustomRole(e.target.value)}
              placeholder="p. ej. Recordatorios de pago"
            />
          </FormField>
          {error ? <p className="text-[12px] text-sens">{error}</p> : null}
        </div>
      </Modal>

      <Modal
        open={connectFor !== null}
        onClose={() => {
          setConnectFor(null)
          setConflict(null)
        }}
        title={`Conectar número · ${connectFor?.name ?? ''}`}
        subtitle="Credenciales de Meta del cliente para este agente."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConnectFor(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void submitConnect()} loading={busy} disabled={!connectValid}>
              Conectar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Número visible (con lada)" htmlFor="wk-num">
            <Input
              value={form.displayNumber}
              onChange={(e) => setForm((f) => ({ ...f, displayNumber: e.target.value }))}
              placeholder="+52 55 1234 5678"
            />
          </FormField>
          <FormField label="Phone number ID" htmlFor="wk-pnid">
            <Input
              value={form.phoneNumberId}
              onChange={(e) => setForm((f) => ({ ...f, phoneNumberId: e.target.value }))}
            />
          </FormField>
          <FormField label="WhatsApp Business Account ID (WABA)" htmlFor="wk-waba">
            <Input
              value={form.wabaId}
              onChange={(e) => setForm((f) => ({ ...f, wabaId: e.target.value }))}
            />
          </FormField>
          <FormField label="Token de acceso (system user)" htmlFor="wk-token">
            <Input
              value={form.accessToken}
              onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
            />
          </FormField>
          {conflict?.bound ? (
            <div className="rounded-xl border border-line bg-warn-bg p-3 text-[12px] text-text-2">
              Ese número ya está conectado a{' '}
              <strong className="font-semibold text-text">{conflict.tenant?.name}</strong>
              {conflict.bot ? ` (${conflict.bot.name})` : ''}
              {conflict.active === false ? ' (inactivo)' : ''}. Libéralo para conectarlo aquí.
              <div className="mt-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void releaseAndRetry()}
                  loading={busy}
                >
                  Liberar y reconectar
                </Button>
              </div>
            </div>
          ) : null}
          {error ? <p className="text-[12px] text-sens">{error}</p> : null}
        </div>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar agente"
        subtitle={`Se desactivará "${deleteTarget?.name ?? ''}" y se liberará su número. El historial se conserva.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => void confirmDelete()} loading={busy}>
              Sí, eliminar
            </Button>
          </>
        }
      >
        {error ? <p className="text-[12px] text-sens">{error}</p> : null}
      </Modal>
    </Card>
  )
}

/** Record the client's Meta-approved appointment-reminder template. Without a
 *  row here the -24h reminder has no template to send and never fires — this is
 *  the operator-side switch that turns it on. */
// The exact body text the operator must create in WhatsApp Manager, so the
// three variables the reminder fills line up with what the code sends.
const REMINDER_TEMPLATE_BODY =
  'Hola {{1}}, le recordamos su cita de {{2}} el {{3}}. Responda a este mensaje para confirmar o reagendar.'

/** Configure the client's appointment-reminder template. WhatsApp only lets a
 *  business message a customer outside the 24 h window with a template Meta has
 *  pre-approved — so the reminder needs one, created in the client's WhatsApp
 *  Manager and recorded here by its exact name. This explains all of that, not
 *  just presents a bare name field. */
function ReminderTemplateCard({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const { data: templates, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'whatsapp-templates'],
    queryFn: () => listWhatsappTemplates(tenantId),
  })
  const current = templates?.find((t) => t.purpose === 'appointment_reminder')

  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('appt_reminder')
  const [language, setLanguage] = useState('es_MX')
  const [showHow, setShowHow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  function startEdit(): void {
    setName(current?.name ?? 'appt_reminder')
    setLanguage(current?.language ?? 'es_MX')
    setMsg(null)
    setEditing(true)
  }

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: ['admin', 'tenant', tenantId, 'whatsapp-templates'],
    })
  }

  async function save(): Promise<void> {
    setBusy(true)
    setMsg(null)
    try {
      await setWhatsappTemplate(tenantId, {
        purpose: 'appointment_reminder',
        name: name.trim(),
        language: language.trim(),
      })
      await refresh()
      setEditing(false)
      setMsg({ ok: true, text: 'Listo. El recordatorio de cita ya se envía con esta plantilla.' })
    } catch (err) {
      setMsg({ ok: false, text: apiErrorMessage(err, 'No se pudo guardar la plantilla.') })
    } finally {
      setBusy(false)
    }
  }

  async function remove(): Promise<void> {
    setBusy(true)
    setMsg(null)
    try {
      await removeWhatsappTemplate(tenantId, 'appointment_reminder')
      await refresh()
      setMsg({ ok: true, text: 'Plantilla quitada. El recordatorio deja de enviarse.' })
    } catch (err) {
      setMsg({ ok: false, text: apiErrorMessage(err, 'No se pudo quitar la plantilla.') })
    } finally {
      setBusy(false)
    }
  }

  // Meta template names are lowercase, digits, underscore.
  const nameValid = /^[a-z0-9_]+$/.test(name.trim())

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[13px] font-semibold text-text">Recordatorio de cita</p>
        {isLoading ? null : current ? (
          <Chip tone="ok" dot>
            Activo
          </Chip>
        ) : (
          <Chip tone="warn" dot>
            Sin configurar
          </Chip>
        )}
      </div>
      <p className="text-[12.5px] leading-relaxed text-text-2">
        Se envía por WhatsApp 24 horas antes de cada cita. Como llega fuera de la ventana de 24 h de
        conversación, WhatsApp exige una plantilla aprobada por Meta. Créala una vez en WhatsApp
        Manager del cliente y registra aquí su nombre.
      </p>

      {/* State + primary actions */}
      {!editing ? (
        <div className="flex flex-wrap items-center gap-3">
          {current ? (
            <>
              <span className="font-mono text-[12px] text-text">{current.name}</span>
              <span className="text-[11px] text-text-2">{current.language}</span>
              <Button variant="secondary" onClick={startEdit} disabled={busy}>
                Cambiar
              </Button>
              <Button variant="ghost" onClick={() => void remove()} loading={busy}>
                Quitar
              </Button>
            </>
          ) : (
            <Button onClick={startEdit}>Registrar plantilla</Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] text-text-2">Nombre de la plantilla</span>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="appt_reminder"
                aria-label="Nombre de la plantilla"
                className="max-w-[220px] font-mono"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10.5px] text-text-2">Idioma</span>
              <Input
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="es_MX"
                aria-label="Idioma de la plantilla"
                className="max-w-[110px] font-mono"
              />
            </label>
            <Button onClick={() => void save()} loading={busy} disabled={!nameValid}>
              Guardar
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              Cancelar
            </Button>
          </div>
          {name.trim().length > 0 && !nameValid ? (
            <p className="text-[12px] text-sens">
              El nombre solo admite minúsculas, números y guion bajo.
            </p>
          ) : null}
        </div>
      )}

      {/* How-to, collapsed by default so it doesn't shout at an operator who
          already did it. */}
      <button
        type="button"
        onClick={() => setShowHow((v) => !v)}
        className="self-start text-[12px] font-medium text-link hover:underline"
      >
        {showHow ? 'Ocultar cómo crearla' : 'Cómo crearla en WhatsApp Manager'}
      </button>
      {showHow ? (
        <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[12px] leading-relaxed text-text-2">
          <li>
            Entra a WhatsApp Manager del cliente → <span className="text-text">Plantillas</span> →{' '}
            <span className="text-text">Crear plantilla</span>.
          </li>
          <li>
            Categoría <span className="text-text">Utility</span>, idioma{' '}
            <span className="font-mono text-text">es_MX</span>, nombre{' '}
            <span className="font-mono text-text">appt_reminder</span>.
          </li>
          <li>
            Cuerpo (con 3 variables):
            <span className="mt-1 block rounded-lg bg-bg p-2 font-mono text-[11px] text-text">
              {REMINDER_TEMPLATE_BODY}
            </span>
            <span className="mt-1 block">
              {'{{1}}'} nombre del paciente · {'{{2}}'} especialidad · {'{{3}}'} fecha y hora.
            </span>
          </li>
          <li>Envíala. Cuando Meta la apruebe (minutos a horas), registra su nombre aquí.</li>
        </ol>
      ) : null}

      {msg ? (
        <p className={`text-[12px] ${msg.ok ? 'text-text-2' : 'text-sens'}`}>{msg.text}</p>
      ) : null}
    </div>
  )
}

/** One Instagram or Messenger channel: its connection state + a connect modal.
 *  Separate card per channel so each reads clearly — not a combined list. The
 *  operator enters the account id + a token from the client's Meta setup; the
 *  token is stored encrypted server-side and never shown back. */
function MetaChannelCard({
  tenantId,
  channel,
  connected,
}: {
  tenantId: string
  channel: MetaChannel
  connected: ConnectedChannel | undefined
}): JSX.Element {
  const queryClient = useQueryClient()
  const label = channel === 'instagram' ? 'Instagram' : 'Messenger'
  const idLabel = channel === 'instagram' ? 'ID de la cuenta de Instagram' : 'ID de la página'

  const [open, setOpen] = useState(false)
  const [externalId, setExternalId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const valid = externalId.trim() !== '' && displayName.trim() !== '' && accessToken.trim() !== ''

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'channels'] })
  }

  async function connect(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      const res = await connectChannel(tenantId, channel, {
        externalId: externalId.trim(),
        displayName: displayName.trim(),
        accessToken: accessToken.trim(),
      })
      await refresh()
      if (!res.webhookSubscribed) {
        // Stored, but Meta didn't accept the subscription — it won't receive
        // messages until the token/permissions are right. Keep the modal open
        // with the reason rather than silently "succeeding".
        setError(
          'Cuenta guardada, pero Meta no aceptó la suscripción del webhook. Revisa que el token tenga permisos de mensajería y reintenta.',
        )
        return
      }
      setOpen(false)
      setExternalId('')
      setDisplayName('')
      setAccessToken('')
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo conectar la cuenta.'))
    } finally {
      setBusy(false)
    }
  }

  async function disconnect(): Promise<void> {
    setBusy(true)
    try {
      await disconnectChannel(tenantId, channel)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <Card.Head title={label} />
      <Card.Body>
        {connected ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <Chip tone="ok" dot>
                Conectado
              </Chip>
              <span className="text-[12.5px] font-medium text-text">{connected.displayName}</span>
              <span className="font-mono text-[10.5px] text-text-2">{connected.externalId}</span>
              <Button variant="secondary" onClick={() => void disconnect()} loading={busy}>
                Desconectar
              </Button>
            </div>
            <p className="text-[12px] text-text-2">
              El agente responde los mensajes de esta cuenta. Las conversaciones aparecen en
              Bandeja, igual que WhatsApp.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-[12.5px] text-text-2">
              {label} no está conectado. Conéctalo para que el agente responda los mensajes de esta
              cuenta.
            </p>
            <Button onClick={() => setOpen(true)}>Conectar {label}</Button>
          </div>
        )}
      </Card.Body>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Conectar ${label}`}
        subtitle={`Datos de la cuenta de ${label} del cliente, desde su panel de Meta (App → ${
          channel === 'instagram' ? 'Instagram' : 'Messenger'
        }).`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void connect()} loading={busy} disabled={!valid}>
              Conectar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label={idLabel} htmlFor={`${channel}-id`}>
            <Input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="17841400000000000"
            />
          </FormField>
          <FormField label="Nombre visible" htmlFor={`${channel}-name`}>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={channel === 'instagram' ? '@usuario' : 'Nombre de la página'}
            />
          </FormField>
          <FormField label="Token de acceso" htmlFor={`${channel}-token`}>
            <Input
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="Token de Meta"
            />
          </FormField>
          <p className="text-[11.5px] text-text-2">
            El token se guarda cifrado y no se vuelve a mostrar.
          </p>
          {error ? <p className="text-[12px] text-sens">{error}</p> : null}
        </div>
      </Modal>
    </Card>
  )
}

/** The Canales tab: one card per channel (WhatsApp / Instagram / Messenger),
 *  each self-contained. Fetches the connected non-WhatsApp channels once and
 *  hands each card its own — so nothing is duplicated across cards. */
function ChannelsTab({ tenantId }: { tenantId: string }): JSX.Element {
  const { data: channels } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'channels'],
    queryFn: () => listConnectedChannels(tenantId),
  })
  const byChannel = (c: MetaChannel): ConnectedChannel | undefined =>
    channels?.find((x) => x.channel === c)

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[12.5px] text-text-2">
        Conecta los canales por los que el agente atenderá a los clientes de este negocio. Cada
        cuenta se conecta una vez; después las conversaciones llegan a Bandeja.
      </p>
      <IdentitiesPanel tenantId={tenantId} />
      <MetaChannelCard tenantId={tenantId} channel="instagram" connected={byChannel('instagram')} />
      <MetaChannelCard tenantId={tenantId} channel="messenger" connected={byChannel('messenger')} />
    </div>
  )
}

function IdentitiesPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const { data: status, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'whatsapp'],
    queryFn: () => getWhatsappStatus(tenantId),
  })
  const {
    data: health,
    isFetching: healthLoading,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'whatsapp', 'health'],
    queryFn: () => checkWhatsappHealth(tenantId),
    enabled: status?.connected === true,
  })
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    displayNumber: '',
    phoneNumberId: '',
    wabaId: '',
    accessToken: '',
  })
  const [testTo, setTestTo] = useState('')
  const [testBusy, setTestBusy] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const valid =
    form.displayNumber.trim() !== '' &&
    form.phoneNumberId.trim() !== '' &&
    form.wabaId.trim() !== '' &&
    form.accessToken.trim() !== ''

  async function submit(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      const result = await connectWhatsapp(tenantId, {
        displayNumber: form.displayNumber.trim(),
        phoneNumberId: form.phoneNumberId.trim(),
        wabaId: form.wabaId.trim(),
        accessToken: form.accessToken.trim(),
      })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'whatsapp'] })
      if (!result.webhookSubscribed) {
        // The number is stored, but Meta didn't accept the webhook subscription
        // (bad token/permissions). It won't receive messages until this works.
        setError(
          'Número guardado, pero no se pudo suscribir el webhook en Meta. Revisa el token y los permisos del WABA, y reintenta.',
        )
        return
      }
      setOpen(false)
      setForm({ displayNumber: '', phoneNumberId: '', wabaId: '', accessToken: '' })
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo conectar. Revisa las credenciales de Meta.'))
    } finally {
      setBusy(false)
    }
  }

  async function disconnect(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      await disconnectWhatsapp(tenantId)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'whatsapp'] })
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo desconectar el número.'))
    } finally {
      setBusy(false)
    }
  }

  async function sendTest(): Promise<void> {
    setTestBusy(true)
    setTestResult(null)
    try {
      const res = await sendWhatsappTest(tenantId, testTo.trim())
      setTestResult(
        res.sent
          ? { ok: true, message: 'Mensaje enviado. Revisa ese WhatsApp.' }
          : {
              ok: false,
              message: res.error?.message
                ? `Meta rechazó el envío: ${res.error.message}`
                : 'No se pudo enviar.',
            },
      )
    } catch (err) {
      setTestResult({ ok: false, message: apiErrorMessage(err, 'No se pudo enviar la prueba.') })
    } finally {
      setTestBusy(false)
    }
  }

  if (isLoading) return <Spinner label="Cargando…" />

  return (
    <Card>
      <Card.Head title="WhatsApp" />
      <Card.Body>
        {status?.connected ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Chip tone="ok" dot>
                Conectado
              </Chip>
              <span className="text-[12.5px] font-medium text-text">{status.phoneNumber}</span>
              <span className="font-mono text-[10.5px] text-text-2">WABA {status.wabaId}</span>
              <Button variant="secondary" onClick={() => void disconnect()} loading={busy}>
                Desconectar
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {healthLoading ? (
                <Chip tone="info">Verificando con Meta…</Chip>
              ) : health?.status === 'active' ? (
                <Chip tone="ok" dot>
                  Token verificado{health.verifiedName ? ` · ${health.verifiedName}` : ''}
                </Chip>
              ) : health?.status === 'token_invalid' ? (
                <Chip tone="warn" dot>
                  Token inválido o expiró — reconecta el número
                </Chip>
              ) : health?.status === 'unknown' ? (
                <Chip tone="warn">No se pudo verificar con Meta</Chip>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => void refetchHealth()}
                loading={healthLoading}
              >
                Verificar
              </Button>
            </div>
            <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-3">
              <p className="text-[12px] text-text-2">
                Envía un mensaje de prueba para confirmar que el agente puede escribir a un número
                (no prueba la recepción de mensajes entrantes).
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="52155..."
                  aria-label="Número de prueba"
                  className="max-w-[200px]"
                />
                <Button
                  variant="secondary"
                  onClick={() => void sendTest()}
                  loading={testBusy}
                  disabled={testTo.trim().length < 6}
                >
                  Enviar prueba
                </Button>
              </div>
              {testResult ? (
                <p className={`text-[12px] ${testResult.ok ? 'text-text-2' : 'text-sens'}`}>
                  {testResult.message}
                </p>
              ) : null}
            </div>
            <ReminderTemplateCard tenantId={tenantId} />
            {error ? <p className="text-[12px] text-sens">{error}</p> : null}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-[12.5px] text-text-2">
              WhatsApp no está conectado. Conéctalo para que el agente responda por WhatsApp y para
              habilitar los recordatorios de cita.
            </p>
            <Button onClick={() => setOpen(true)}>Conectar WhatsApp</Button>
          </div>
        )}
      </Card.Body>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Conectar WhatsApp"
        subtitle="Credenciales de Meta del cliente (WhatsApp → API Setup en su panel de Meta)."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void submit()} loading={busy} disabled={!valid}>
              Conectar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Número visible (con lada)" htmlFor="wa-number">
            <Input
              value={form.displayNumber}
              onChange={(e) => setForm((f) => ({ ...f, displayNumber: e.target.value }))}
              placeholder="+52 55 1234 5678"
            />
          </FormField>
          <FormField label="Phone number ID" htmlFor="wa-pnid">
            <Input
              value={form.phoneNumberId}
              onChange={(e) => setForm((f) => ({ ...f, phoneNumberId: e.target.value }))}
            />
          </FormField>
          <FormField label="WhatsApp Business Account ID (WABA)" htmlFor="wa-waba">
            <Input
              value={form.wabaId}
              onChange={(e) => setForm((f) => ({ ...f, wabaId: e.target.value }))}
            />
          </FormField>
          <FormField label="Token de acceso (system user)" htmlFor="wa-token">
            <Input
              value={form.accessToken}
              onChange={(e) => setForm((f) => ({ ...f, accessToken: e.target.value }))}
            />
          </FormField>
          {error ? <p className="text-[12px] text-sens">{error}</p> : null}
        </div>
      </Modal>
    </Card>
  )
}

const MEMBER_STATUS_BADGE: Record<
  TenantMember['status'],
  { tone: 'ok' | 'draft' | 'warn'; label: string }
> = {
  active: { tone: 'ok', label: 'Activo' },
  inactive: { tone: 'draft', label: 'Inactivo' },
  pending: { tone: 'warn', label: 'Pendiente' },
}

// Read-only client roster (operator "Equipo" tab). The client manages its own
// team elsewhere; here a gerente only sees who to help.
/** Operator manages a client's providers (doctors). The concierge sets up the
 *  clinic on the client's behalf; without active providers the bot can't offer
 *  or book a single appointment. Weekly schedules are a follow-up (slice 2). */
function ProvidersPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const { data: providers, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'providers'],
    queryFn: () => listProviders(tenantId),
  })

  const [editing, setEditing] = useState<Provider | 'new' | null>(null)
  const [scheduling, setScheduling] = useState<Provider | null>(null)
  const [name, setName] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [location, setLocation] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  function open(target: Provider | 'new'): void {
    setEditing(target)
    setError('')
    if (target === 'new') {
      setName('')
      setSpecialty('')
      setLocation('')
    } else {
      setName(target.name)
      setSpecialty(target.specialty)
      setLocation(target.location ?? '')
    }
  }

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'providers'] })
  }

  const valid = name.trim() !== '' && specialty.trim() !== ''

  async function save(): Promise<void> {
    setBusy(true)
    setError('')
    try {
      if (editing === 'new') {
        await createProvider(tenantId, {
          name: name.trim(),
          specialty: specialty.trim(),
          ...(location.trim() ? { location: location.trim() } : {}),
        })
      } else if (editing) {
        await updateProvider(tenantId, editing.id, {
          name: name.trim(),
          specialty: specialty.trim(),
          location: location.trim() || null,
        })
      }
      await refresh()
      setEditing(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo guardar el doctor.'))
    } finally {
      setBusy(false)
    }
  }

  async function toggleStatus(p: Provider): Promise<void> {
    setBusy(true)
    try {
      await updateProvider(tenantId, p.id, {
        status: p.status === 'active' ? 'inactive' : 'active',
      })
      await refresh()
    } catch {
      toast.error('No se pudo actualizar el doctor', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <Card.Head
        title="Doctores"
        action={<Button onClick={() => open('new')}>Nuevo doctor</Button>}
      />
      <Card.Body>
        {isLoading ? (
          <Spinner label="Cargando…" />
        ) : !providers || providers.length === 0 ? (
          <p className="text-[12.5px] text-text-2">
            Este cliente aún no tiene doctores. Agrega al menos uno para que el agente pueda ofrecer
            y agendar citas.
          </p>
        ) : (
          <ul className="flex flex-col">
            {providers.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center gap-3 border-b border-line py-3 last:border-b-0"
              >
                <span className="text-[12.5px] font-medium text-text sm:w-48 sm:flex-none">
                  {p.name}
                </span>
                <span className="text-[12px] text-text-2 sm:w-40 sm:flex-none">{p.specialty}</span>
                {p.location ? (
                  <span className="text-[11.5px] text-text-2">{p.location}</span>
                ) : null}
                <Chip tone={p.status === 'active' ? 'ok' : 'draft'} dot>
                  {p.status === 'active' ? 'Activo' : 'Inactivo'}
                </Chip>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="secondary" onClick={() => setScheduling(p)} disabled={busy}>
                    Horarios
                  </Button>
                  <Button variant="secondary" onClick={() => open(p)} disabled={busy}>
                    Editar
                  </Button>
                  <Button variant="ghost" onClick={() => void toggleStatus(p)} loading={busy}>
                    {p.status === 'active' ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card.Body>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Nuevo doctor' : 'Editar doctor'}
        subtitle="Los doctores del cliente. Se usan para ofrecer y agendar citas."
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void save()} loading={busy} disabled={!valid}>
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Nombre" htmlFor="prov-name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dra. Ana Ruiz"
            />
          </FormField>
          <FormField label="Especialidad" htmlFor="prov-spec">
            <Input
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              placeholder="Cardiología"
            />
          </FormField>
          <FormField label="Sucursal (opcional)" htmlFor="prov-loc">
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Consultorio 3"
            />
          </FormField>
          {error ? <p className="text-[12px] text-sens">{error}</p> : null}
        </div>
      </Modal>

      {scheduling ? (
        <ProviderScheduleModal
          tenantId={tenantId}
          provider={scheduling}
          onClose={() => setScheduling(null)}
        />
      ) : null}
    </Card>
  )
}

// Operator-side weekly hours + time-off for one client's doctor. Mirrors the
// owner's AvailabilityModal, reusing the shared MultiBlockWeekEditor; only the
// data seam is cross-tenant (getProviderSchedule/... take a tenantId).
function ProviderScheduleModal({
  tenantId,
  provider,
  onClose,
}: {
  tenantId: string
  provider: Provider
  onClose: () => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const scheduleKey = ['admin', 'tenant', tenantId, 'provider', provider.id, 'schedule']
  const { data, isLoading } = useQuery({
    queryKey: scheduleKey,
    queryFn: () => getProviderSchedule(tenantId, provider.id),
  })
  const [week, setWeek] = useState<DayBlocks[]>([])
  const [slotMinutes, setSlotMinutes] = useState(30)
  const [busy, setBusy] = useState(false)

  // Seed the week editor ONCE from the initial load. Adding/removing time-off
  // refetches this same query (it returns blocks + timeOff together), so
  // re-seeding on every `data` change would silently wipe the operator's
  // unsaved weekly-hours edits. The time-off list still updates live because
  // ProviderTimeOffSection reads the refetched `data.timeOff` directly.
  const seeded = useRef(false)
  useEffect(() => {
    if (data && !seeded.current) {
      seeded.current = true
      setWeek(buildWeek(data.blocks))
      setSlotMinutes(data.blocks[0]?.slotMinutes ?? 30)
    }
  }, [data])

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: scheduleKey })

  async function save(): Promise<void> {
    setBusy(true)
    try {
      await saveProviderSchedule(tenantId, provider.id, serializeWeek(week, slotMinutes))
      toast.ok('Horario guardado')
      refresh()
      onClose()
    } catch {
      toast.error('No se pudo guardar el horario', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Horarios de ${provider.name}`}
      subtitle="Define las horas de atención por día y la duración de cada consulta."
      size="wide"
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button className="ml-auto" onClick={() => void save()} disabled={busy || isLoading}>
            {busy ? 'Guardando…' : 'Guardar horario'}
          </Button>
        </>
      }
    >
      {isLoading || !data ? (
        <Spinner label="Cargando horario…" />
      ) : (
        <div className="flex flex-col gap-5">
          <MultiBlockWeekEditor week={week} onChange={setWeek} />
          <FormField label="Duración de cada consulta (minutos)" htmlFor="prov-slot-min">
            <NumberInput
              id="prov-slot-min"
              value={slotMinutes}
              onChange={setSlotMinutes}
              min={5}
              max={240}
              step={5}
            />
          </FormField>
          <ProviderTimeOffSection
            tenantId={tenantId}
            provider={provider}
            timeOff={data.timeOff}
            onChange={refresh}
          />
        </div>
      )}
    </Modal>
  )
}

function ProviderTimeOffSection({
  tenantId,
  provider,
  timeOff,
  onChange,
}: {
  tenantId: string
  provider: Provider
  timeOff: ProviderTimeOff[]
  onChange: () => void
}): JSX.Element {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [reason, setReason] = useState('')

  async function add(): Promise<void> {
    if (!from || !to) return
    try {
      await addProviderTimeOff(tenantId, provider.id, {
        startsAt: new Date(`${from}T00:00:00`).toISOString(),
        endsAt: new Date(`${to}T23:59:59`).toISOString(),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
      setFrom('')
      setTo('')
      setReason('')
      onChange()
    } catch {
      toast.error('No se pudo bloquear esas fechas')
    }
  }

  async function remove(id: string): Promise<void> {
    try {
      await deleteProviderTimeOff(tenantId, provider.id, id)
      onChange()
    } catch {
      toast.error('No se pudo quitar el bloqueo')
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <p className="text-sm font-medium">Días bloqueados (vacaciones, ausencias)</p>
      {timeOff.length === 0 ? (
        <p className="text-sm text-text-3">Sin bloqueos.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {timeOff.map((t) => (
            <li key={t.id} className="flex items-center justify-between text-sm">
              <span>
                {fmtDateTime(t.startsAt)} — {fmtDateTime(t.endsAt)}
                {t.reason ? ` · ${t.reason}` : ''}
              </span>
              <Button size="sm" variant="ghost" onClick={() => void remove(t.id)}>
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Desde" htmlFor="prov-to-from">
          <Input
            id="prov-to-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormField>
        <FormField label="Hasta" htmlFor="prov-to-to">
          <Input id="prov-to-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
        <FormField label="Motivo" htmlFor="prov-to-reason" required="optional">
          <Input id="prov-to-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <Button size="sm" variant="secondary" onClick={() => void add()} disabled={!from || !to}>
          Bloquear
        </Button>
      </div>
    </div>
  )
}

// Operator-side catalog (estudios / servicios / precios) for a client. Mirrors
// the owner Catálogo page, reusing the cross-tenant listCatalog/... seam. Prices
// are pesos on the wire; the API stores cents.
const catalogMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

function CatalogPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient()
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'catalog'],
    queryFn: () => listCatalog(tenantId),
  })
  const [editing, setEditing] = useState<CatalogItem | 'new' | null>(null)
  const [busy, setBusy] = useState(false)

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'catalog'] })
  }

  async function toggleActive(item: CatalogItem): Promise<void> {
    setBusy(true)
    try {
      await updateCatalogItem(tenantId, item.id, { active: !item.active })
      await refresh()
    } catch {
      toast.error('No se pudo actualizar el elemento', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <Card.Head
        title="Catálogo"
        action={<Button onClick={() => setEditing('new')}>Nuevo elemento</Button>}
      />
      <Card.Body>
        {isLoading ? (
          <Spinner label="Cargando…" />
        ) : !items || items.length === 0 ? (
          <p className="text-[12.5px] text-text-2">
            Este cliente aún no tiene catálogo. Agrega estudios o servicios con su precio para que
            el agente los cotice.
          </p>
        ) : (
          <ul className="flex flex-col">
            {items.map((it) => (
              <li
                key={it.id}
                className="flex flex-wrap items-center gap-3 border-b border-line py-3 last:border-b-0"
              >
                <span className="text-[12.5px] font-medium text-text sm:w-56 sm:flex-none">
                  {it.name}
                </span>
                <span className="text-[12px] text-text-2 sm:w-32 sm:flex-none">
                  {it.category ?? '—'}
                </span>
                <span className="text-[12.5px] font-medium text-text tabular-nums">
                  {catalogMxn.format(it.priceMxn)}
                </span>
                <Chip tone={it.active ? 'ok' : 'draft'} dot>
                  {it.active ? 'Activo' : 'Inactivo'}
                </Chip>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="secondary" onClick={() => setEditing(it)} disabled={busy}>
                    Editar
                  </Button>
                  <Button variant="ghost" onClick={() => void toggleActive(it)} loading={busy}>
                    {it.active ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card.Body>

      {editing ? (
        <CatalogItemModal
          tenantId={tenantId}
          item={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refresh()
          }}
        />
      ) : null}
    </Card>
  )
}

function CatalogItemModal({
  tenantId,
  item,
  onClose,
  onSaved,
}: {
  tenantId: string
  item?: CatalogItem | undefined
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const [name, setName] = useState(item?.name ?? '')
  const [category, setCategory] = useState(item?.category ?? '')
  const [priceMxn, setPriceMxn] = useState(item?.priceMxn ?? 0)
  const [description, setDescription] = useState(item?.description ?? '')
  const [active, setActive] = useState<boolean>(item?.active ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const isEdit = Boolean(item)

  async function submit(): Promise<void> {
    if (!name.trim()) return
    setBusy(true)
    setError('')
    try {
      if (item) {
        await updateCatalogItem(tenantId, item.id, {
          name: name.trim(),
          category: category.trim() || null,
          priceMxn,
          description: description.trim() || null,
          active,
        })
      } else {
        await createCatalogItem(tenantId, {
          name: name.trim(),
          priceMxn,
          ...(category.trim() ? { category: category.trim() } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
        })
      }
      onSaved()
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo guardar el elemento.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Editar elemento' : 'Nuevo elemento'}
      subtitle={
        isEdit ? 'Actualiza el estudio o servicio.' : 'Agrega un estudio o servicio con su precio.'
      }
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={!name.trim()}>
            {isEdit ? 'Guardar' : 'Agregar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Nombre" htmlFor="cat-name" required>
          <Input
            id="cat-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Resonancia magnética"
          />
        </FormField>
        <FormField label="Categoría" htmlFor="cat-cat" required="optional">
          <Input
            id="cat-cat"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Estudio, Servicio, Consulta…"
          />
        </FormField>
        <FormField label="Precio (MXN)" htmlFor="cat-price">
          <NumberInput
            id="cat-price"
            value={priceMxn}
            onChange={setPriceMxn}
            min={0}
            currency="MXN"
          />
        </FormField>
        <FormField label="Descripción" htmlFor="cat-desc" required="optional">
          <Textarea
            id="cat-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
        {isEdit ? (
          <FormField label="Estado" htmlFor="cat-status">
            <Select
              id="cat-status"
              options={[
                { value: 'active', label: 'Activo' },
                { value: 'inactive', label: 'Inactivo' },
              ]}
              value={active ? 'active' : 'inactive'}
              onChange={(e) => setActive(e.target.value === 'active')}
            />
          </FormField>
        ) : null}
        {error ? <p className="text-[12px] text-sens">{error}</p> : null}
      </div>
    </Modal>
  )
}

function TeamPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const qc = useQueryClient()
  const { data: members, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'members'],
    queryFn: () => getTenantMembers(tenantId),
  })
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'tenant_admin' | 'tenant_agent' | 'tenant_viewer'>(
    'tenant_agent',
  )
  const [removeFor, setRemoveFor] = useState<{ id: string; name: string; email: string } | null>(
    null,
  )
  const [busy, setBusy] = useState(false)
  const [panelError, setPanelError] = useState<string | null>(null)

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'members'] })
  }

  async function run(fn: () => Promise<void>, okText: string, failText: string): Promise<void> {
    setBusy(true)
    setPanelError(null)
    try {
      await fn()
      await refresh()
      setInviteOpen(false)
      setRemoveFor(null)
      toast.ok(okText)
    } catch (err) {
      setPanelError(apiErrorMessage(err, failText))
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) return <Spinner label="Cargando…" />

  return (
    <Card>
      <Card.Head
        title="Equipo del cliente"
        action={
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            Invitar miembro
          </Button>
        }
      />
      <Card.Body>
        {panelError && !inviteOpen && !removeFor ? (
          <div className="mb-3">
            <Banner tone="warn">{panelError}</Banner>
          </div>
        ) : null}
        {members && members.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {members.map((m) => {
              const s = MEMBER_STATUS_BADGE[m.status]
              const isOwner = m.role === 'tenant_owner'
              return (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2.5"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[12.5px] font-semibold text-text">
                      {m.name || 'Invitación pendiente'}
                    </span>
                    <a className="text-[11px] text-link hover:underline" href={`mailto:${m.email}`}>
                      {m.email}
                    </a>
                  </span>
                  <span className="ml-auto flex flex-wrap items-center gap-2">
                    {/* The owner is untouchable (server rule); pending invites
                        have no membership to edit yet. */}
                    {!isOwner && m.status === 'active' ? (
                      <Select
                        aria-label={`Rol de ${m.name || m.email}`}
                        value={m.role}
                        disabled={busy}
                        onChange={(e) =>
                          void run(
                            () =>
                              changeTenantMemberRole(
                                tenantId,
                                m.id,
                                e.target.value as 'tenant_admin' | 'tenant_agent' | 'tenant_viewer',
                              ),
                            'Rol actualizado. La persona vuelve a iniciar sesión.',
                            'No se pudo cambiar el rol.',
                          )
                        }
                        options={[
                          { value: 'tenant_admin', label: 'Administración' },
                          { value: 'tenant_agent', label: 'Asesor' },
                          { value: 'tenant_viewer', label: 'Solo lectura' },
                        ]}
                      />
                    ) : (
                      <Chip tone="draft">{m.roleLabel}</Chip>
                    )}
                    <Chip tone={s.tone} dot>
                      {s.label}
                    </Chip>
                    {!isOwner && m.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => setRemoveFor({ id: m.id, name: m.name, email: m.email })}
                      >
                        Quitar
                      </Button>
                    ) : null}
                  </span>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-[12.5px] text-text-2">
            Este cliente aún no tiene miembros en su equipo.
          </p>
        )}
      </Card.Body>

      <Modal
        open={inviteOpen}
        onClose={() => {
          if (!busy) setInviteOpen(false)
        }}
        title="Invitar miembro"
        subtitle="La persona recibe un correo con su enlace de acceso."
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviteOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                void run(
                  () =>
                    inviteTenantMember(tenantId, { email: inviteEmail.trim(), role: inviteRole }),
                  'Invitación enviada.',
                  'No se pudo enviar la invitación.',
                )
              }
            >
              Enviar invitación
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Correo electrónico" htmlFor="team-invite-email">
            <Input
              id="team-invite-email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="recepcion@clinica.mx"
              type="email"
            />
          </FormField>
          <FormField label="Rol" htmlFor="team-invite-role">
            <Select
              id="team-invite-role"
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(e.target.value as 'tenant_admin' | 'tenant_agent' | 'tenant_viewer')
              }
              options={[
                { value: 'tenant_agent', label: 'Asesor (atiende conversaciones)' },
                { value: 'tenant_admin', label: 'Administración (gestiona el espacio)' },
                { value: 'tenant_viewer', label: 'Solo lectura' },
              ]}
            />
          </FormField>
          {panelError && <Banner tone="warn">{panelError}</Banner>}
        </div>
      </Modal>

      <Modal
        open={removeFor !== null}
        onClose={() => {
          if (!busy) setRemoveFor(null)
        }}
        title="Quitar del equipo"
        subtitle={`${removeFor?.name || removeFor?.email || ''} pierde el acceso de inmediato; sus conversaciones vuelven a la cola.`}
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveFor(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                void run(
                  () => removeTenantMember(tenantId, removeFor!.id),
                  'Miembro quitado.',
                  'No se pudo quitar al miembro.',
                )
              }
            >
              Quitar
            </Button>
          </>
        }
      >
        {panelError && <Banner tone="warn">{panelError}</Banner>}
      </Modal>
    </Card>
  )
}

export function TenantDetailPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  // Same pattern as the client Settings page: unknown values fall back to
  // Resumen, and the default keeps a clean URL. Validated against EVERY tab id
  // (including the healthcare-only ones) because the tenant — and therefore its
  // vertical — has not loaded on first render.
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const tab = requestedTab !== null && ALL_TAB_IDS.has(requestedTab) ? requestedTab : 'overview'
  const setTab = (next: string): void => {
    setSearchParams(next === 'overview' ? {} : { tab: next }, { replace: true })
  }

  const queryClient = useQueryClient()
  const { data: tenant, isLoading } = useQuery({
    queryKey: ['admin', 'tenant', id],
    queryFn: () => getTenant(id),
  })

  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [editName, setEditName] = useState('')
  const [editPlan, setEditPlan] = useState<TenantPlan>('profesional')
  const [editAm, setEditAm] = useState('')
  const [confirmName, setConfirmName] = useState('')
  const [goLiveError, setGoLiveError] = useState('')
  // The same assessor the gate runs. Only fetched while the client is still
  // onboarding — once live, the checklist's job is done.
  const readinessQuery = useQuery({
    queryKey: ['admin', 'tenant', id, 'go-live-readiness'],
    queryFn: () => getGoLiveReadiness(id),
    enabled: Boolean(tenant?.onboarding),
  })
  const readiness = readinessQuery.data ?? null
  const [resendState, setResendState] = useState<'idle' | 'busy' | 'sent' | 'err'>('idle')
  // Surfaced when an edit / suspend / delete fails — without this the modal just
  // sat there silently and the action looked like it "did nothing".
  const [opError, setOpError] = useState('')

  // The pool of possible account managers = our INHERTA team (platform users).
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['admin', 'org-users'],
    queryFn: listOrgUsers,
    enabled: editing,
  })

  const refetch = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', id] }).then(() => undefined)

  function openEdit(): void {
    if (!tenant) return
    setEditName(tenant.name)
    setEditPlan(tenant.plan)
    setEditAm('')
    setOpError('')
    setEditing(true)
  }

  async function saveEdit(): Promise<void> {
    setBusy(true)
    setOpError('')
    try {
      await updateTenant(id, {
        name: editName.trim(),
        plan: editPlan,
        ...(editAm ? { accountManagerId: editAm } : {}),
      })
      await refetch()
      setEditing(false)
    } catch (err) {
      setOpError(apiErrorMessage(err, 'No se pudo guardar el cliente.'))
    } finally {
      setBusy(false)
    }
  }

  async function toggleSuspend(): Promise<void> {
    if (!tenant) return
    setBusy(true)
    setOpError('')
    try {
      await (tenant.suspended ? reactivateTenant(id) : suspendTenant(id))
      await refetch()
    } catch (err) {
      setOpError(apiErrorMessage(err, 'No se pudo cambiar el estado del cliente.'))
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete(): Promise<void> {
    setBusy(true)
    setOpError('')
    try {
      await deleteTenant(id, confirmName)
      // Drop the now-stale list + this tenant's cache so the table doesn't show
      // the just-deleted client until a manual reload.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      queryClient.removeQueries({ queryKey: ['admin', 'tenant', id] })
      await navigate('/admin/tenants')
    } catch (err) {
      setOpError(apiErrorMessage(err, 'No se pudo eliminar el cliente.'))
    } finally {
      setBusy(false)
    }
  }

  async function goLive(): Promise<void> {
    setBusy(true)
    setGoLiveError('')
    try {
      await goLiveTenant(id)
      await refetch()
      await readinessQuery.refetch()
    } catch (err) {
      setGoLiveError(
        apiErrorMessage(err, 'No se pudo publicar. Conecta WhatsApp antes de publicar.'),
      )
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl py-10">
        <Spinner label="Cargando cliente…" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PageHeader title="Cliente no encontrado" />
        <div className="mt-6">
          <EmptyStateCard
            icon={<ClockIcon />}
            title="No encontramos este cliente"
            body="Puede que haya sido eliminado o que el enlace sea incorrecto."
            actions={
              <Button onClick={() => void navigate('/admin/tenants')}>Volver a clientes</Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div data-testid="admin-tenant-detail" className="mx-auto w-full max-w-5xl">
      <PageHeader
        breadcrumb={[{ label: 'Clientes', href: '/admin/tenants' }]}
        title={tenant.name}
        actions={
          <div className="flex items-center gap-2">
            {tenant.suspended ? (
              <Chip tone="warn" dot>
                Suspendido
              </Chip>
            ) : (
              <Chip tone={STATUS_BADGE[tenant.status].tone} dot>
                {STATUS_BADGE[tenant.status].label}
              </Chip>
            )}
            {tenant.onboarding ? (
              <Button
                size="sm"
                loading={busy}
                disabled={readiness !== null && !readiness.ready}
                title={
                  readiness !== null && !readiness.ready
                    ? 'Faltan pasos — revisa "Camino a en vivo" en Resumen.'
                    : undefined
                }
                onClick={() => void goLive()}
              >
                Publicar
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={openEdit}>
              Editar
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void toggleSuspend()}
            >
              {tenant.suspended ? 'Reactivar' : 'Suspender'}
            </Button>
            <DropdownMenu
              trigger={
                <button
                  type="button"
                  aria-label="Más acciones del cliente"
                  className="flex size-8 flex-none items-center justify-center rounded-md border border-line text-text-3 transition-colors hover:bg-hover hover:text-text [&_svg]:size-[16px]"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <circle cx="12" cy="5" r="1.7" />
                    <circle cx="12" cy="12" r="1.7" />
                    <circle cx="12" cy="19" r="1.7" />
                  </svg>
                </button>
              }
              items={[
                {
                  label: 'Eliminar cliente',
                  danger: true,
                  onSelect: () => {
                    setConfirmName('')
                    setOpError('')
                    setDeleting(true)
                  },
                },
              ]}
            />
          </div>
        }
      />
      {tenant.ownerInvitePending ? (
        <div className="mt-4">
          <Banner tone="warn">
            El Dueño aún no activó su cuenta. Se le envió una invitación por correo
            {tenant.ownerInviteEmail ? (
              <>
                {' a '}
                <strong>{tenant.ownerInviteEmail}</strong>
              </>
            ) : null}
            .
            <div className="mt-2.5 flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                loading={resendState === 'busy'}
                disabled={resendState === 'busy'}
                onClick={() => {
                  setResendState('busy')
                  resendOwnerInvite(tenant.id)
                    .then(() => setResendState('sent'))
                    .catch(() => setResendState('err'))
                }}
              >
                Reenviar invitación
              </Button>
              {resendState === 'sent' ? (
                <span className="text-[12.5px] text-text-2">
                  Invitación reenviada con un enlace nuevo.
                </span>
              ) : null}
              {resendState === 'err' ? (
                <span className="text-[12.5px] text-sens" role="alert">
                  No se pudo reenviar. Intenta de nuevo.
                </span>
              ) : null}
            </div>
          </Banner>
        </div>
      ) : null}
      {goLiveError ? (
        <p className="mt-1 text-[12px] text-sens" role="alert">
          {goLiveError}
        </p>
      ) : null}
      {opError && !editing && !deleting ? (
        <p className="mt-1 text-[12px] text-sens" role="alert">
          {opError}
        </p>
      ) : null}

      <div className="mt-4">
        <Tabs
          tabs={tabsFor(tenant.vertical)}
          activeId={tab}
          onChange={setTab}
          idBase="tenant-detail"
          aria-label="Secciones del cliente"
        />
      </div>

      <div className="mt-5">
        <TabPanel idBase="tenant-detail" tabId={tab}>
          {tab === 'overview' ? (
            <div className="flex flex-col gap-4">
              {tenant.onboarding && (
                <GoLiveChecklist
                  readiness={readiness}
                  loading={readinessQuery.isLoading}
                  onGoToTab={setTab}
                />
              )}
              <Overview tenant={tenant} />
            </div>
          ) : tab === 'bots' ? (
            <WorkersPanel tenantId={tenant.id} />
          ) : tab === 'identities' ? (
            <div className="flex flex-col gap-4">
              <ChannelsTab tenantId={tenant.id} />
              {/* The consent notice belongs with the channels: it is the first
                  thing that goes out on any of them. */}
              <ConsentSettings tenantId={tenant.id} />
            </div>
          ) : tab === 'agenda' ? (
            <ProvidersPanel tenantId={tenant.id} />
          ) : tab === 'catalog' ? (
            <CatalogPanel tenantId={tenant.id} />
          ) : tab === 'team' ? (
            <TeamPanel tenantId={tenant.id} />
          ) : tab === 'billing' ? (
            <div className="flex flex-col gap-4">
              <BillingPanel
                tenantId={tenant.id}
                tenantName={tenant.name}
                onGoToProfile={() => setTab('overview')}
              />
              <CfdiPanel tenantId={tenant.id} />
            </div>
          ) : tab === 'conversations' ? (
            <ConversationsPanel tenantId={tenant.id} />
          ) : tab === 'privacy' ? (
            <PrivacyRequests tenantId={tenant.id} />
          ) : (
            <EmptyStateCard
              icon={<ClockIcon />}
              title="Próximamente"
              body="Esta pestaña está en construcción."
            />
          )}
        </TabPanel>
      </div>

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title="Editar cliente"
        subtitle="Actualiza el nombre o el plan."
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={() => void saveEdit()}
              loading={busy}
              disabled={editName.trim().length === 0}
            >
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Nombre del cliente" htmlFor="edit-tenant-name">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
          </FormField>
          <FormField label="Plan" htmlFor="edit-tenant-plan">
            <Select
              value={editPlan}
              onChange={(e) => setEditPlan(e.target.value as TenantPlan)}
              options={(Object.keys(PLAN_LABEL) as TenantPlan[]).map((p) => ({
                value: p,
                label: PLAN_LABEL[p],
              }))}
            />
          </FormField>
          <FormField
            label="Gerente de cuenta (equipo INHERTA)"
            htmlFor="edit-tenant-am"
            helper={`Actual: ${tenant.accountManager.name}`}
          >
            <Select
              value={editAm}
              onChange={(e) => setEditAm(e.target.value)}
              options={[
                { value: '', label: 'Mantener gerente actual' },
                ...orgUsers.map((u) => ({ value: u.id, label: `${u.name} · ${u.email}` })),
              ]}
            />
          </FormField>
          {opError ? (
            <p className="text-[12px] text-sens" role="alert">
              {opError}
            </p>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Eliminar cliente"
        subtitle="Esta acción es permanente y borra todos sus datos."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDelete()}
              loading={busy}
              disabled={confirmName !== tenant.name}
            >
              Eliminar definitivamente
            </Button>
          </>
        }
      >
        <FormField
          label={`Escribe "${tenant.name}" para confirmar`}
          htmlFor="delete-tenant-confirm"
        >
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={tenant.name}
          />
        </FormField>
        {opError ? (
          <p className="mt-3 text-[12px] text-sens" role="alert">
            {opError}
          </p>
        ) : null}
      </Modal>
    </div>
  )
}

/* ── Facturación (operator view of ONE client, Phase 3a.8) ──────────────────
 * The dashboard answers "how is the business doing"; this answers "what is
 * going on with THIS client" — the question an account manager actually gets
 * asked on a call. Read + the three changes that were previously impossible:
 * reprice, convert to/from courtesy, and cancel.
 */
const SUB_STATUS_LABEL: Record<
  string,
  { label: string; tone: 'ok' | 'warn' | 'sens' | 'info' | 'draft' }
> = {
  active: { label: 'Activa', tone: 'ok' },
  comped: { label: 'Cortesía', tone: 'info' },
  pending_payment_setup: { label: 'Falta primer pago', tone: 'warn' },
  past_due: { label: 'Pago vencido', tone: 'warn' },
  suspended: { label: 'Suspendida', tone: 'sens' },
  cancelled: { label: 'Cancelada', tone: 'draft' },
}

/** Centavos → "$15,000.00". */
type ChipTone = 'ok' | 'warn' | 'sens' | 'info' | 'draft'

/**
 * "Camino a en vivo": the go-live preconditions as a live checklist,
 * fed by the SAME assessor the Publicar gate runs. Before this card the
 * preconditions were discoverable only by clicking Publicar and reading one
 * 400 at a time. Each pending step links to the tab where it gets fixed.
 */
function GoLiveChecklist({
  readiness,
  loading,
  onGoToTab,
}: {
  readiness: GoLiveReadiness | null
  loading: boolean
  onGoToTab: (tab: string) => void
}): JSX.Element {
  const steps =
    readiness === null
      ? []
      : [
          {
            key: 'bot',
            label: 'Agente creado',
            done: readiness.bot,
            hint: 'Crea el agente del cliente.',
            tab: 'bots',
            tabLabel: 'Ir a Agentes',
            hard: true,
          },
          {
            key: 'channel',
            label: 'Canal conectado (WhatsApp, Instagram o Messenger)',
            done: readiness.channel,
            hint: 'Conecta al menos un canal.',
            tab: 'identities',
            tabLabel: 'Ir a Canales',
            hard: true,
          },
          {
            key: 'billingEmail',
            label: 'Correo de facturación',
            done: readiness.profile.billingEmail,
            hint: 'Sin él no se puede crear el cobro. Está en Perfil del negocio, aquí abajo.',
            tab: null,
            tabLabel: null,
            hard: false,
          },
          {
            key: 'subscription',
            label: 'Suscripción creada',
            done: readiness.subscription,
            hint: 'Crea la suscripción con los términos de la llamada.',
            tab: 'billing',
            tabLabel: 'Ir a Facturación',
            hard: true,
          },
          {
            key: 'paid',
            label: 'Primer pago recibido (o cortesía)',
            done: readiness.paid,
            hint: 'Comparte el enlace de pago; publicamos en cuanto entre.',
            tab: 'billing',
            tabLabel: 'Ir a Facturación',
            hard: true,
          },
          {
            key: 'fiscal',
            label: 'Datos fiscales (para el CFDI)',
            done: readiness.profile.fiscal,
            hint: 'No detiene la publicación, pero el CFDI queda pendiente sin ellos.',
            tab: null,
            tabLabel: null,
            hard: false,
          },
        ]
  const pendingHard = steps.filter((st) => st.hard && !st.done).length

  return (
    <Card>
      <Card.Head
        title="Camino a en vivo"
        sub={
          readiness?.ready
            ? 'Todo listo: Publicar está habilitado arriba.'
            : 'Lo que falta para publicar a este cliente. Publicar se habilita cuando todo esté en verde.'
        }
      />
      <Card.Body>
        {loading || readiness === null ? (
          <Spinner label="Revisando requisitos…" />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {steps.map((st) => (
              <li
                key={st.key}
                className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0 last:pb-0"
              >
                <Chip tone={st.done ? 'ok' : st.hard ? 'warn' : 'info'} dot>
                  {st.done ? 'Listo' : 'Pendiente'}
                </Chip>
                <span className="text-[13px] font-medium text-text">{st.label}</span>
                {!st.done && <span className="text-[12px] text-text-2">{st.hint}</span>}
                {!st.done && st.tab !== null && (
                  <span className="ml-auto">
                    <Button size="sm" variant="secondary" onClick={() => onGoToTab(st.tab)}>
                      {st.tabLabel}
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {readiness !== null && !readiness.ready && pendingHard > 0 && (
          <p className="mt-3 text-[12px] text-text-3">
            {pendingHard === 1
              ? 'Falta 1 paso obligatorio.'
              : `Faltan ${pendingHard} pasos obligatorios.`}
          </p>
        )}
      </Card.Body>
    </Card>
  )
}

const INVOICE_STATUS: Record<string, { label: string; tone: ChipTone }> = {
  draft: { label: 'Borrador', tone: 'draft' },
  open: { label: 'Por pagar', tone: 'warn' },
  paid: { label: 'Pagada', tone: 'ok' },
  void: { label: 'Anulada', tone: 'draft' },
  uncollectible: { label: 'Incobrable', tone: 'sens' },
}

/**
 * Every Stripe invoice for the client, payable ones first-class. The
 * CFDI panel below only shows STAMPED documents, so an open invoice — the one
 * the salesperson needs mid-call — was invisible on this tab. "Copiar enlace
 * de pago" is the handover: paste the hosted-invoice URL into the chat and
 * the client pays before the call ends.
 */
function InvoicesCard({ tenantId }: { tenantId: string }): JSX.Element {
  const { data, isLoading, isError, refetch } = useQuery({
    // Same key as CfdiPanel — one fetch feeds both cards.
    queryKey: ['admin', 'tenant', tenantId, 'cfdis'],
    queryFn: () => getTenantCfdis(tenantId),
  })
  const [copyingId, setCopyingId] = useState<string | null>(null)

  async function copyLink(inv: TenantCfdiInvoice): Promise<void> {
    setCopyingId(inv.invoiceId)
    try {
      const url = await getInvoicePayLink(tenantId, inv.invoiceId)
      try {
        await navigator.clipboard.writeText(url)
        toast.ok('Enlace de pago copiado', {
          description: 'Pégalo en el chat con el cliente.',
        })
      } catch {
        // Clipboard denied (http, permissions): still hand the URL over.
        toast.ok('Enlace de pago listo', { description: url })
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo obtener el enlace de pago.'))
    } finally {
      setCopyingId(null)
    }
  }

  return (
    <Card>
      <Card.Head
        title="Facturas"
        sub="Los cobros de Stripe. El enlace de pago es lo que le compartes al cliente en la llamada."
      />
      <Card.Body>
        {isError ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-[12.5px] text-text-2">No se pudieron cargar las facturas.</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : isLoading ? (
          <Spinner label="Cargando facturas…" />
        ) : !data || data.length === 0 ? (
          <p className="text-[12.5px] text-text-2">
            Sin facturas todavía. Aparecen aquí en cuanto Stripe emite la primera.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {data.map((inv) => {
              const st = INVOICE_STATUS[inv.invoiceStatus] ?? {
                label: inv.invoiceStatus,
                tone: 'draft' as ChipTone,
              }
              return (
                <li
                  key={inv.invoiceId}
                  className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0 last:pb-0"
                >
                  <Chip tone={st.tone} dot>
                    {st.label}
                  </Chip>
                  <span className="text-[13px] font-semibold text-text">
                    {inv.folio ?? 'Sin folio'}
                  </span>
                  <span className="text-[12px] text-text-2">
                    {mxn(inv.amountTotalMxn)}
                    {inv.paidAt !== null &&
                      ` · pagada ${new Date(inv.paidAt).toLocaleDateString('es-MX')}`}
                  </span>
                  {inv.invoiceStatus === 'open' && (
                    <span className="ml-auto">
                      <Button
                        size="sm"
                        loading={copyingId === inv.invoiceId}
                        onClick={() => void copyLink(inv)}
                      >
                        Copiar enlace de pago
                      </Button>
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Card.Body>
    </Card>
  )
}

/** Local yyyy-mm-dd — NOT toISOString(), which is UTC and rolls to tomorrow
 *  during a Mexican evening. Same class of bug as the CFDI DATE columns. */
function localIsoDate(d = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

const CYCLE_OPTIONS = [
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral (paga 3 meses por adelantado)' },
  { value: 'annual', label: 'Anual (paga 12 meses por adelantado)' },
]

/**
 * The close-the-deal control. Ordered the way the sales call goes:
 * negotiated price first — the seeded 'standard' tier has NO base price by
 * design, every real deal is bespoke — then the prepay lever (cycle), the
 * signed commitment, and only then the comp escape hatch. Preflight surfaces
 * the two server-side rejections (billing email; fiscal data → CFDI pending)
 * BEFORE the operator fills five fields and loses the form to a 400.
 */
function CreateSubscriptionCard({
  tenantId,
  tenantName,
  onGoToProfile,
}: {
  tenantId: string
  tenantName: string
  onGoToProfile: () => void
}): JSX.Element {
  const qc = useQueryClient()
  // Shares the ProfilePanel cache key, so opening Facturación right after
  // Resumen renders the preflight instantly.
  const profile = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'business-profile'],
    queryFn: () => getBusinessProfile(tenantId),
  })
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'paid' | 'comped'>('paid')
  const [price, setPrice] = useState('')
  const [cycle, setCycle] = useState('monthly')
  const [start, setStart] = useState(localIsoDate())
  const [endMin, setEndMin] = useState('')
  const [discount, setDiscount] = useState('')
  const [compReason, setCompReason] = useState('')
  const [compUntil, setCompUntil] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const missingEmail = !profile.isLoading && !profile.data?.contactEmail
  const missingFiscal =
    !profile.isLoading &&
    profile.data !== undefined &&
    (!profile.data.rfc ||
      !profile.data.fiscalPostalCode ||
      !profile.data.fiscalRegimen ||
      !profile.data.cfdiUse)

  function localError(): string | null {
    if (kind === 'paid' && (!price || Number(price) <= 0)) {
      return 'Escribe el precio negociado.'
    }
    if (!endMin) return 'Falta la fecha de compromiso mínimo.'
    if (endMin <= start) return 'El compromiso mínimo debe terminar después del inicio.'
    if (kind === 'comped' && !compReason.trim()) return 'Una cortesía necesita un motivo.'
    if (discount && (Number(discount) < 0 || Number(discount) > 100)) {
      return 'El descuento va de 0 a 100.'
    }
    return null
  }

  async function create(): Promise<void> {
    const invalid = localError()
    if (invalid) {
      setErr(invalid)
      return
    }
    setBusy(true)
    setErr(null)
    try {
      await createTenantSubscription(tenantId, {
        tierKey: 'standard',
        status: kind === 'paid' ? 'pending_payment_setup' : 'comped',
        contractStart: start,
        contractEndMin: endMin,
        billingCycle: cycle as 'monthly' | 'quarterly' | 'annual',
        ...(kind === 'paid' ? { customPriceMxn: Math.round(Number(price) * 100) } : {}),
        ...(discount ? { discountPct: Number(discount) } : {}),
        ...(kind === 'comped'
          ? { compReason: compReason.trim(), compUntil: compUntil || null }
          : {}),
      })
      await qc.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'subscription'] })
      setOpen(false)
      toast.ok('Suscripción creada', {
        description:
          kind === 'paid'
            ? 'La factura de Stripe llegará al correo de facturación del cliente.'
            : 'Cortesía activa, sin cobro.',
      })
    } catch (e) {
      setErr(apiErrorMessage(e, 'No se pudo crear la suscripción.'))
    } finally {
      setBusy(false)
    }
  }

  function goProfile(): void {
    setOpen(false)
    onGoToProfile()
  }

  return (
    <>
      <Card>
        <Card.Head
          title="Sin suscripción"
          sub={`${tenantName} todavía no tiene un plan contratado.`}
        />
        <Card.Body>
          <p className="text-[12.5px] leading-relaxed text-text-2">
            Crea la suscripción con los términos que cerraste en la llamada: precio negociado, ciclo
            de cobro y compromiso mínimo. El cliente recibe la factura por correo y el agente se
            publica cuando el pago entra.
          </p>
          <div className="mt-4">
            <Button onClick={() => setOpen(true)}>Crear suscripción</Button>
          </div>
        </Card.Body>
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Crear suscripción"
        subtitle={`Términos del contrato de ${tenantName}.`}
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button loading={busy} onClick={() => void create()}>
              Crear suscripción
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {kind === 'paid' && missingEmail && (
            <Banner tone="warn">
              <div className="flex flex-col items-start gap-1.5">
                <span>
                  Falta el correo de facturación del cliente — sin él no se puede crear el cobro.
                </span>
                <button
                  type="button"
                  className="text-[12px] font-semibold text-link underline-offset-2 hover:underline"
                  onClick={goProfile}
                >
                  Completar en Perfil del negocio
                </button>
              </div>
            </Banner>
          )}
          {kind === 'paid' && !missingEmail && missingFiscal && (
            <Banner tone="info">
              <div className="flex flex-col items-start gap-1.5">
                <span>
                  Faltan datos fiscales (RFC, código postal, régimen o uso CFDI): la factura fiscal
                  quedará pendiente hasta completarlos.
                </span>
                <button
                  type="button"
                  className="text-[12px] font-semibold text-link underline-offset-2 hover:underline"
                  onClick={goProfile}
                >
                  Completar en Perfil del negocio
                </button>
              </div>
            </Banner>
          )}

          <FormField label="Tipo" htmlFor="sub-kind">
            <Select
              id="sub-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'paid' | 'comped')}
              options={[
                { value: 'paid', label: 'De pago' },
                { value: 'comped', label: 'Cortesía (sin cobro)' },
              ]}
            />
          </FormField>

          {kind === 'paid' && (
            <>
              <FormField
                label="Precio negociado (MXN al mes, sin IVA)"
                htmlFor="sub-create-price"
                helper={
                  price && Number(price) > 0
                    ? `Con IVA: ${mxn(Math.round(Number(price) * 100 * 1.16))} al mes.`
                    : 'El precio que cerraste en la llamada.'
                }
              >
                <Input
                  id="sub-create-price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ''))}
                  inputMode="numeric"
                  placeholder="15000"
                />
              </FormField>
              <FormField
                label="Ciclo de cobro"
                htmlFor="sub-cycle"
                helper="Trimestral y anual cobran el periodo completo por adelantado."
              >
                <Select
                  id="sub-cycle"
                  value={cycle}
                  onChange={(e) => setCycle(e.target.value)}
                  options={CYCLE_OPTIONS}
                />
              </FormField>
            </>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Inicio del contrato" htmlFor="sub-start">
              <Input
                id="sub-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </FormField>
            <FormField
              label="Compromiso mínimo hasta"
              htmlFor="sub-end-min"
              helper="Lo que el cliente firmó."
            >
              <Input
                id="sub-end-min"
                type="date"
                value={endMin}
                onChange={(e) => setEndMin(e.target.value)}
              />
            </FormField>
          </div>

          {kind === 'paid' && (
            <FormField label="Descuento (%)" htmlFor="sub-discount" helper="Opcional.">
              <Input
                id="sub-discount"
                value={discount}
                onChange={(e) => setDiscount(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                placeholder="0"
              />
            </FormField>
          )}

          {kind === 'comped' && (
            <>
              <FormField label="Motivo" htmlFor="sub-comp-reason" helper="Queda en la bitácora.">
                <Input
                  id="sub-comp-reason"
                  value={compReason}
                  onChange={(e) => setCompReason(e.target.value)}
                  placeholder="Piloto de 3 meses"
                />
              </FormField>
              <FormField
                label="Cortesía hasta"
                htmlFor="sub-comp-until"
                helper="Opcional: cuándo revisar esta cortesía."
              >
                <Input
                  id="sub-comp-until"
                  type="date"
                  value={compUntil}
                  onChange={(e) => setCompUntil(e.target.value)}
                />
              </FormField>
            </>
          )}

          {err && <Banner tone="warn">{err}</Banner>}
        </div>
      </Modal>
    </>
  )
}

function mxn(centavos: number): string {
  return (centavos / 100).toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  })
}

function BillingPanel({
  tenantId,
  tenantName,
  onGoToProfile,
}: {
  tenantId: string
  tenantName: string
  /** Jumps to the Resumen tab, where Perfil del negocio lives — the fix for
   *  both preflight warnings (billing email, fiscal data). */
  onGoToProfile: () => void
}): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'subscription'],
    queryFn: () => getTenantSubscription(tenantId),
  })
  const [priceOpen, setPriceOpen] = useState(false)
  const [price, setPrice] = useState('')
  const [compOpen, setCompOpen] = useState(false)
  const [compReason, setCompReason] = useState('')
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [immediate, setImmediate] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  async function run(fn: () => Promise<unknown>, okText: string, failText: string): Promise<void> {
    setBusy(true)
    setMsg(null)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId, 'subscription'] })
      setMsg({ ok: true, text: okText })
      setPriceOpen(false)
      setCompOpen(false)
      setCancelOpen(false)
    } catch (err) {
      setMsg({ ok: false, text: apiErrorMessage(err, failText) })
    } finally {
      setBusy(false)
    }
  }

  if (isError) {
    return (
      <Card>
        <Card.Body>
          <div className="flex flex-col items-start gap-2">
            <p className="text-[12.5px] text-text-2">No se pudo cargar la suscripción.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        </Card.Body>
      </Card>
    )
  }
  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <Spinner label="Cargando suscripción…" />
        </Card.Body>
      </Card>
    )
  }
  if (!data) {
    return (
      <CreateSubscriptionCard
        tenantId={tenantId}
        tenantName={tenantName}
        onGoToProfile={onGoToProfile}
      />
    )
  }

  const st = SUB_STATUS_LABEL[data.status] ?? { label: data.status, tone: 'draft' as const }
  const isComped = data.status === 'comped'

  return (
    <div className="flex flex-col gap-3">
      {msg && <Banner tone={msg.ok ? 'ok' : 'warn'}>{msg.text}</Banner>}
      <Card>
        <Card.Head title="Suscripción" sub={`Plan y cobro de ${tenantName}.`} />
        <Card.Body>
          <MetaRow k="Estado">
            <Chip tone={st.tone} dot>
              {st.label}
            </Chip>
          </MetaRow>
          <MetaRow k="Plan">{data.tierKey}</MetaRow>
          <MetaRow k="Precio">
            {data.priceMxn !== null ? (
              <span>
                {mxn(data.priceMxn)} / {data.billingCycle === 'monthly' ? 'mes' : data.billingCycle}
                <span className="ml-1.5 text-[11px] text-text-3">
                  + IVA ({mxn(Math.round(data.priceMxn * 1.16))} total)
                </span>
              </span>
            ) : (
              <Muted>Sin precio</Muted>
            )}
          </MetaRow>
          <MetaRow k="Contrato">
            {data.contractStart} → mínimo {data.contractEndMin}
          </MetaRow>
          <MetaRow k="Stripe">
            {data.stripeSubscriptionId ? (
              <span className="font-mono text-[11px]">{data.stripeSubscriptionId}</span>
            ) : (
              <Muted>{isComped ? 'Sin cobro (cortesía)' : 'Sin suscripción en Stripe'}</Muted>
            )}
          </MetaRow>
          {data.compReason && <MetaRow k="Motivo de cortesía">{data.compReason}</MetaRow>}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPrice(data.priceMxn !== null ? String(Math.round(data.priceMxn / 100)) : '')
                setPriceOpen(true)
              }}
            >
              Cambiar precio
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setCompReason(data.compReason ?? '')
                setCompOpen(true)
              }}
            >
              {isComped ? 'Convertir a pago' : 'Dar cortesía'}
            </Button>
            {data.status !== 'cancelled' && (
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)}>
                Cancelar suscripción
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      <InvoicesCard tenantId={tenantId} />

      <Modal
        open={priceOpen}
        onClose={() => setPriceOpen(false)}
        title="Cambiar el precio"
        subtitle="Un aumento se cobra prorrateado de inmediato; una baja aplica en el siguiente ciclo."
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPriceOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                void run(
                  () =>
                    patchTenantSubscription(tenantId, {
                      customPriceMxn: Math.round(Number(price) * 100),
                    }),
                  'Precio actualizado.',
                  'No se pudo cambiar el precio.',
                )
              }
            >
              Guardar
            </Button>
          </>
        }
      >
        <FormField label="Precio mensual (MXN, sin IVA)" htmlFor="sub-price">
          <Input
            id="sub-price"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            placeholder="15000"
          />
        </FormField>
      </Modal>

      <Modal
        open={compOpen}
        onClose={() => setCompOpen(false)}
        title={isComped ? 'Convertir a suscripción de pago' : 'Dar cortesía'}
        subtitle={
          isComped
            ? 'Se crea el cobro en Stripe y el cliente empieza a recibir facturas.'
            : 'Dejamos de facturarle y el servicio sigue activo.'
        }
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCompOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                void run(
                  () =>
                    patchTenantSubscription(
                      tenantId,
                      isComped
                        ? { status: 'active' }
                        : { status: 'comped', compReason: compReason.trim() },
                    ),
                  isComped ? 'Ahora es una suscripción de pago.' : 'Cortesía aplicada.',
                  'No se pudo cambiar el tipo de suscripción.',
                )
              }
            >
              Confirmar
            </Button>
          </>
        }
      >
        {isComped ? (
          <p className="text-[12.5px] leading-relaxed text-text-2">
            Se creará el cliente y la suscripción en Stripe con el precio actual, y la próxima
            factura se emitirá normalmente.
          </p>
        ) : (
          <FormField label="Motivo" htmlFor="comp-reason" helper="Queda en la bitácora.">
            <Input
              id="comp-reason"
              value={compReason}
              onChange={(e) => setCompReason(e.target.value)}
              placeholder="Piloto de 3 meses"
            />
          </FormField>
        )}
      </Modal>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancelar la suscripción"
        subtitle="Cancelar de inmediato también detiene al agente del cliente."
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)} disabled={busy}>
              No cancelar
            </Button>
            <Button
              loading={busy}
              onClick={() =>
                void run(
                  () =>
                    cancelTenantSubscription(
                      tenantId,
                      immediate ? 'immediate' : 'at_period_end',
                      cancelReason.trim() || undefined,
                    ),
                  immediate
                    ? 'Suscripción cancelada y agente detenido.'
                    : 'Se cancelará al final del periodo pagado.',
                  'No se pudo cancelar.',
                )
              }
            >
              Cancelar suscripción
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3.5">
          <FormField label="Motivo" htmlFor="cancel-reason" required="optional">
            <Input
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Cambió a un competidor"
            />
          </FormField>
          <label className="flex items-start gap-2 text-[12.5px] text-text-2">
            <input
              type="checkbox"
              checked={immediate}
              onChange={(e) => setImmediate(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Cancelar de inmediato y detener al agente.
              <span className="block text-[11px] text-text-3">
                Sin esto, el servicio sigue hasta el final del periodo que ya pagaron.
              </span>
            </span>
          </label>
        </div>
      </Modal>
    </div>
  )
}

/* ── Conversaciones (operator, Phase 3a.9) ───────────────────────────────────
 * The way IN to the diagnosis view. /admin/conversations/:id was already built
 * and already had a backend, but nothing linked to it: this tab was disabled and
 * no other screen listed a tenant's conversations, so troubleshooting a client's
 * bot required knowing a UUID by heart.
 */
const CONV_STATE_LABEL: Record<
  string,
  { label: string; tone: 'ok' | 'warn' | 'sens' | 'info' | 'draft' }
> = {
  active_bot: { label: 'Con el agente', tone: 'ok' },
  waiting_handoff: { label: 'Esperando asesor', tone: 'warn' },
  active_handoff: { label: 'Con un asesor', tone: 'info' },
  closed: { label: 'Cerrada', tone: 'draft' },
  archived: { label: 'Archivada', tone: 'draft' },
}

function ConversationsPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'tenant', tenantId, 'conversations'],
    queryFn: () => listTenantConversations(tenantId),
  })

  if (isError) {
    return (
      <Card>
        <Card.Body>
          <div className="flex flex-col items-start gap-2">
            <p className="text-[12.5px] text-text-2">No se pudieron cargar las conversaciones.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        </Card.Body>
      </Card>
    )
  }
  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <Spinner label="Cargando conversaciones…" />
        </Card.Body>
      </Card>
    )
  }
  if (!data || data.length === 0) {
    return (
      <EmptyStateCard
        icon={<ClockIcon />}
        title="Sin conversaciones"
        body="Este cliente todavía no tiene conversaciones. Aparecerán aquí en cuanto alguien le escriba a su agente."
      />
    )
  }

  return (
    <Card>
      <Card.Head
        title="Conversaciones recientes"
        sub="Abre la traza para ver qué respondió el agente y por qué."
      />
      <ul className="flex flex-col divide-y divide-line">
        {data.map((c) => {
          const st = CONV_STATE_LABEL[c.state] ?? { label: c.state, tone: 'draft' as const }
          return (
            <li key={c.id} className="flex items-center gap-3 px-[18px] py-3">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-text">
                  {c.contact}
                </span>
                <span className="mt-px block text-[11px] text-text-3">
                  {c.channel} ·{' '}
                  {new Date(c.lastActivityAt).toLocaleString('es-MX', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </span>
              <Chip tone={st.tone} dot>
                {st.label}
              </Chip>
              <Button
                variant="secondary"
                size="sm"
                className="flex-none"
                onClick={() => void navigate(`/admin/conversations/${c.id}`)}
              >
                Ver traza
              </Button>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
